/**
 * Outil « Tableau de proportionnalité » (Boîte à outils, 6e → 3e).
 *
 * Tableau : des situations (pommes, taxi, recette, carré…) ou son propre
 * tableau ; les quotients ligne du bas ÷ ligne du haut disent si c'est
 * proportionnel (coefficient) et le graphique le montre (droite par
 * l'origine ; alignés sans passer par l'origine pour le taxi).
 * Valeur manquante : quatrième proportionnelle par coefficient, retour à
 * l'unité, linéarité, et dès la 4e produit en croix, avec les flèches.
 * Partage (dès la 4e) : un ratio a : b (: c) en parts égales.
 * Calculs : `proportion.ts`.
 */

import { useState } from 'react';
import { Shuffle } from 'lucide-react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  IconButton,
  Note,
  NumberField,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { div, exact, type Frac } from '../../ui/exact';
import { frenchSpaces } from '../../ui/format';
import { arrowHeadPoints, FIGURE_BG_CLASS, HaloText, INK, MUTED } from '../../ui/svg-parts';
import {
  analyse,
  fx,
  graphSay,
  linearFactor,
  linearityCheck,
  n,
  METHOD_LABELS,
  missingSteps,
  missingValue,
  randomMissing,
  shareSteps,
  show,
  SITUATIONS,
  type Column,
  type Method,
  type Missing,
  type Step,
} from './proportion';

type Mode = 'table' | 'missing' | 'share';

const C1 = 'var(--color-primary-500)';
const C2 = 'var(--color-accent-500)';
const C3 = 'var(--color-success-600)';
const LINE = 'var(--color-ink-300)';
const VB_W = 340;

export function ProportionTableTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = Math.min(4, Math.max(1, schoolLevelRank(level) ?? 3));
  const [mode, setMode] = useState<Mode>('table');
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'table', label: 'Tableau' },
    { value: 'missing', label: 'Valeur manquante' },
    ...(rank >= 3 ? [{ value: 'share' as const, label: 'Partage' }] : []),
  ];
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Mode" value={mode} options={options} onChange={setMode} />
      <div hidden={mode !== 'table'} className="space-y-4">
        <TableMode rank={rank} />
      </div>
      <div hidden={mode !== 'missing'} className="space-y-4">
        <MissingMode rank={rank} rng={rng} onSend={onSend} sendCost={sendCost} />
      </div>
      {rank >= 3 && (
        <div hidden={mode !== 'share'} className="space-y-4">
          <ShareMode onSend={onSend} sendCost={sendCost} />
        </div>
      )}
    </div>
  );
}

function StepList({ steps, testId }: { steps: Step[]; testId?: string }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      {steps.map((s, i) => (
        <div key={i} className="space-y-0.5">
          <MathText className="text-ink-900 block overflow-x-auto py-0.5 text-lg">{`$${s.tex}$`}</MathText>
          <p className="text-ink-600 text-sm">{frenchSpaces(s.say)}</p>
        </div>
      ))}
    </div>
  );
}

const sendLabel = (f: Frac) => exact(f).plain;

/* ------------------------------------------------------------------ */
/* Tableau                                                             */
/* ------------------------------------------------------------------ */

const parse = (t: string) => {
  const v = Number(t.replace(/\s/g, '').replace('−', '-').replace(',', '.'));
  return t.trim() !== '' && Number.isFinite(v) ? Number(v.toFixed(3)) : null;
};

interface OwnTable {
  top: string;
  bottom: string;
  cols: Column[];
}

function TableMode({ rank }: { rank: number }) {
  const [sitId, setSitId] = useState('pommes');
  const [own, setOwn] = useState<OwnTable>({
    top: 'Ligne 1',
    bottom: 'Ligne 2',
    cols: [
      { x: 2, y: 3 },
      { x: 4, y: 6 },
      { x: 5, y: 7.5 },
    ],
  });
  const sit = SITUATIONS.find((s) => s.id === sitId);
  const cols = sit ? sit.cols : own.cols;
  const top = sit?.top ?? own.top;
  const bottom = sit?.bottom ?? own.bottom;
  const a = analyse(cols, rank);
  const lin = linearityCheck(cols);
  // éditer une case fait passer à « Mon tableau », copie de la situation avec ses titres
  const edit = (i: number, key: 'x' | 'y', v: number) => {
    const next = cols.map((c, j) => (j === i ? { ...c, [key]: v } : c));
    setOwn({ top, bottom, cols: next });
    setSitId('own');
  };
  // nouvelle colonne : la suivante, proportionnelle si le tableau l'est
  const addColumn = () =>
    setOwn((o) => {
      const last = o.cols.at(-1)!;
      const x = Number((last.x + 1).toFixed(3));
      const k = a.proportional && a.coef ? a.coef.p / a.coef.q : null;
      return {
        ...o,
        cols: [...o.cols, { x, y: k !== null ? Number((x * k).toFixed(3)) : last.y + 1 }],
      };
    });
  return (
    <>
      <label className="block">
        <span className="text-ink-700 text-sm font-semibold">Situation</span>
        <select
          value={sitId}
          onChange={(e) => setSitId(e.target.value)}
          className="border-ink-200 text-ink-900 mt-1 min-h-11 w-full rounded-xl border bg-transparent px-2 text-sm"
        >
          {SITUATIONS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
          <option value="own">Mon tableau</option>
        </select>
      </label>
      <EditableTable
        cols={cols}
        top={top}
        bottom={bottom}
        ratios={a.ratios}
        ratioLabel={rank <= 1 ? 'pour 1' : 'bas ÷ haut'}
        onEdit={edit}
      />
      {!sit && (
        <div className="flex flex-wrap gap-2">
          {own.cols.length < 5 && (
            <button type="button" className={SECONDARY_BUTTON} onClick={addColumn}>
              Ajouter une colonne
            </button>
          )}
          {own.cols.length > 2 && (
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => setOwn((o) => ({ ...o, cols: o.cols.slice(0, -1) }))}
            >
              Retirer la dernière
            </button>
          )}
        </div>
      )}
      <Note tone={a.proportional ? 'success' : 'neutral'}>
        <span data-testid="table-say">{frenchSpaces(a.say)}</span>
        {lin && (
          <span className="mt-1 block" data-testid="table-linear">
            {frenchSpaces(lin.say)}
          </span>
        )}
        {!a.proportional && sit?.why && <span className="mt-1 block">{frenchSpaces(sit.why)}</span>}
      </Note>
      {/* le graphique de la proportionnalité : dès la 5e */}
      {rank >= 2 && (
        <>
          <GraphFigure
            cols={cols}
            coef={a.proportional ? a.coef : null}
            aligned={a.aligned}
            top={top}
            bottom={bottom}
          />
          <p className="text-ink-600 text-sm" data-testid="graph-say">
            {frenchSpaces(graphSay(a, cols.length))}
          </p>
        </>
      )}
    </>
  );
}

function EditableTable({
  cols,
  top,
  bottom,
  ratios,
  ratioLabel,
  onEdit,
}: {
  cols: readonly Column[];
  top: string;
  bottom: string;
  ratios: Array<Frac | null>;
  ratioLabel: string;
  onEdit: (i: number, key: 'x' | 'y', v: number) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse text-center text-sm">
        <colgroup>
          <col className="w-24" />
        </colgroup>
        <tbody>
          {(['x', 'y'] as const).map((key) => (
            <tr key={key}>
              <th
                scope="row"
                className="border-ink-200 text-ink-700 border px-1 py-1 text-left text-xs font-semibold"
              >
                {key === 'x' ? top : bottom}
              </th>
              {cols.map((c, i) => (
                <td key={i} className="border-ink-200 border p-0">
                  <CellInput
                    value={c[key]}
                    label={`${key === 'x' ? top : bottom}, colonne ${i + 1}`}
                    onChange={(v) => onEdit(i, key, v)}
                  />
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <th scope="row" className="text-ink-500 px-1 py-1 text-left text-xs font-normal">
              {ratioLabel}
            </th>
            {ratios.map((r, i) => (
              <td
                key={i}
                className="text-ink-600 px-1 py-1 text-xs tabular-nums"
                data-testid={`ratio-${i}`}
              >
                {r ? show(r) : '—'}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CellInput({
  value,
  label,
  onChange,
}: {
  value: number;
  label: string;
  onChange: (v: number) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={text ?? n(value)}
      onChange={(e) => {
        setText(e.target.value);
        const v = parse(e.target.value);
        if (v !== null && Math.abs(v) <= 100000) onChange(v);
      }}
      onBlur={() => setText(null)}
      className="text-ink-900 font-display bg-ink-50 focus-visible:ring-primary-500 min-h-11 w-full text-center text-base font-semibold tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-inset"
    />
  );
}

function GraphFigure({
  cols,
  coef,
  aligned,
  top,
  bottom,
}: {
  cols: readonly Column[];
  coef: Frac | null;
  aligned: boolean;
  top: string;
  bottom: string;
}) {
  const W = VB_W;
  const H = 220;
  const x0 = 40;
  const y0 = H - 30;
  const w = W - x0 - 16;
  const h = y0 - 20;
  const sorted = [...cols].sort((p, q) => p.x - q.x);
  const first = sorted[0]!;
  const last = sorted.at(-1)!;
  const intercept =
    last.x !== first.x ? first.y - ((last.y - first.y) / (last.x - first.x)) * first.x : 0;
  const shown = !coef && aligned && first.x !== last.x;
  const ys = [...cols.map((c) => c.y), ...(shown ? [intercept] : [])];
  const maxX = Math.max(1, ...cols.map((c) => c.x)) * 1.15;
  const maxY = Math.max(1, ...ys) * 1.15;
  const minX = Math.min(0, ...cols.map((c) => c.x));
  const minY = Math.min(0, ...ys) * 1.15;
  const X = (v: number) => x0 + ((v - minX) / (maxX - minX)) * w;
  const Y = (v: number) => y0 - ((v - minY) / (maxY - minY)) * h;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Graphique : ${cols.map((c) => `(${n(c.x)} ; ${n(c.y)})`).join(', ')}.`}
    >
      <line x1={X(minX)} x2={x0 + w} y1={Y(0)} y2={Y(0)} stroke={MUTED} strokeWidth={1.5} />
      <line x1={X(0)} x2={X(0)} y1={y0} y2={20} stroke={MUTED} strokeWidth={1.5} />
      <HaloText x={x0 + w} y={Y(0) - 8} textAnchor="end" fontSize={12} fill={MUTED}>
        {top}
      </HaloText>
      <text x={X(0) + 6} y={16} fontSize={12} fill={MUTED}>
        {bottom}
      </text>
      <text x={X(0) - 4} y={Y(0) + 14} textAnchor="end" fontSize={12} fill={INK}>
        0
      </text>
      {coef && (
        <line
          x1={X(0)}
          y1={Y(0)}
          x2={X(maxX)}
          y2={Y(maxX * (coef.p / coef.q))}
          stroke={C1}
          strokeWidth={2}
        />
      )}
      {shown && (
        // la droite prolongée jusqu'à l'axe : on voit qu'elle ne passe pas par 0
        <>
          <line
            x1={X(0)}
            y1={Y(intercept)}
            x2={X(last.x)}
            y2={Y(last.y)}
            stroke={C2}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
          <circle
            cx={X(0)}
            cy={Y(intercept)}
            r={5}
            fill="var(--fig-bg, #fff)"
            stroke={C2}
            strokeWidth={2}
          />
          <HaloText x={X(0) + 8} y={Y(intercept) + 16} fontSize={12} fontWeight={700} fill={INK}>
            {n(intercept)}
          </HaloText>
        </>
      )}
      {cols.map((c, i) => (
        <g key={i}>
          <line x1={X(c.x)} x2={X(c.x)} y1={Y(0)} y2={Y(c.y)} stroke={LINE} strokeDasharray="2 3" />
          <circle cx={X(c.x)} cy={Y(c.y)} r={5} fill={coef ? C1 : C2} />
          <HaloText x={X(c.x)} y={Y(0) + 16} textAnchor="middle" fontSize={12} fill={INK}>
            {n(c.x)}
          </HaloText>
          <HaloText x={X(c.x) - 6} y={Y(c.y) - 8} textAnchor="end" fontSize={12} fill={INK}>
            {n(c.y)}
          </HaloText>
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Valeur manquante                                                    */
/* ------------------------------------------------------------------ */

function MissingMode({
  rank,
  rng,
  onSend,
  sendCost,
}: ToolProps & { rank: number; rng: () => number }) {
  const [m, setM] = useState<Missing>({ x1: 3, y1: 4.5, x2: 6 });
  // 6e : linéarité et retour à l'unité (ni coefficient ni produit en croix) ;
  // 5e : + coefficient ; dès la 4e : + égalité des rapports (ad = bc)
  const methods: Method[] =
    rank <= 1
      ? ['linear', 'unit']
      : rank === 2
        ? ['coef', 'unit', 'linear']
        : ['coef', 'unit', 'linear', 'cross'];
  const [picked, setMethod] = useState<Method>(methods[0]!);
  const method = methods.includes(picked) ? picked : methods[0]!;
  const result = missingValue(m);
  const steps = missingSteps(m, method);
  return (
    <>
      <div className="flex items-start gap-2">
        <div className="grid flex-1 grid-cols-2 gap-2">
          <NumberField
            label="Haut 1"
            value={m.x1}
            min={0.5}
            max={1000}
            step={1}
            decimals={2}
            onChange={(x1) => setM({ ...m, x1 })}
          />
          <NumberField
            label="Haut 2"
            value={m.x2}
            min={0.5}
            max={1000}
            step={1}
            decimals={2}
            onChange={(x2) => setM({ ...m, x2 })}
          />
          <NumberField
            label="Bas 1"
            value={m.y1}
            min={0}
            max={100000}
            step={1}
            decimals={2}
            onChange={(y1) => setM({ ...m, y1 })}
          />
        </div>
        <IconButton label="Nouvel exemple" onClick={() => setM(randomMissing(rng))}>
          <Shuffle className="h-5 w-5" aria-hidden />
        </IconButton>
      </div>
      <Segmented
        ariaLabel="Méthode"
        value={method}
        options={methods.map((v) => ({ value: v, label: METHOD_LABELS[v] }))}
        onChange={setMethod}
      />
      <MissingFigure m={m} method={method} result={result} />
      {method === 'linear' && !linearFactor(m) && (
        <Note live={false}>
          {frenchSpaces(
            'La linéarité est commode quand une colonne est un multiple simple de l’autre (× 2, ÷ 3…).',
          )}
        </Note>
      )}
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3" aria-live="polite">
        <StepList steps={steps} testId="missing-steps" />
        {onSend && (
          <SendToSheetButton
            label={sendLabel(result)}
            cost={sendCost}
            onClick={() => onSend({ atoms: exact(result).atoms })}
          />
        )}
      </section>
    </>
  );
}

function MissingFigure({ m, method, result }: { m: Missing; method: Method; result: Frac }) {
  const unit = method === 'unit';
  const cells = unit
    ? [
        { top: n(m.x1), bot: n(m.y1) },
        { top: '1', bot: show(div(fx(m.y1), fx(m.x1))) },
        { top: n(m.x2), bot: show(result) },
      ]
    : [
        { top: n(m.x1), bot: n(m.y1) },
        { top: n(m.x2), bot: show(result) },
      ];
  const cw = unit ? 78 : 96;
  const x0 = (VB_W - cells.length * cw) / 2 - (method === 'coef' ? 30 : 0);
  const yTop = 46;
  const rowH = 40;
  const H = 170;
  const lf = linearFactor(m);
  const k = div(fx(m.y1), fx(m.x1));
  const arc = (xa: number, xb: number, y: number, up: boolean, label: string, color: string) => {
    const mid = (xa + xb) / 2;
    const dy = up ? -26 : 26;
    const d = `M ${xa} ${y} Q ${mid} ${y + dy} ${xb} ${y}`;
    return (
      <g>
        <path d={d} fill="none" stroke={color} strokeWidth={2} />
        <polygon
          points={arrowHeadPoints(mid + (xb - mid) * 0.6, y + dy * 0.4, xb, y, 9)}
          fill={color}
        />
        <HaloText
          x={mid}
          y={y + dy * (up ? 0.75 : 0.95) + (up ? 0 : 6)}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={color}
        >
          {label}
        </HaloText>
      </g>
    );
  };
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Tableau : ${n(m.x1)} donne ${n(m.y1)} ; ${n(m.x2)} donne ${show(result)}. Méthode : ${METHOD_LABELS[method]}.`}
      data-testid="missing-figure"
    >
      {cells.map((c, i) => (
        <g key={i}>
          <rect
            x={x0 + i * cw}
            y={yTop}
            width={cw}
            height={rowH}
            fill="none"
            stroke={INK}
            strokeWidth={1.2}
          />
          <rect
            x={x0 + i * cw}
            y={yTop + rowH}
            width={cw}
            height={rowH}
            fill="none"
            stroke={INK}
            strokeWidth={1.2}
          />
          <text
            x={x0 + (i + 0.5) * cw}
            y={yTop + rowH / 2 + 5}
            textAnchor="middle"
            fontSize={15}
            fill={INK}
          >
            {c.top}
          </text>
          <text
            x={x0 + (i + 0.5) * cw}
            y={yTop + rowH * 1.5 + 5}
            textAnchor="middle"
            fontSize={15}
            fontWeight={i === cells.length - 1 ? 800 : 400}
            fill={i === cells.length - 1 ? C1 : INK}
          >
            {c.bot}
          </text>
        </g>
      ))}
      {method === 'coef' &&
        (() => {
          const xr = x0 + cells.length * cw + 10;
          return (
            <g>
              <path
                d={`M ${xr} ${yTop + rowH / 2} Q ${xr + 26} ${yTop + rowH} ${xr} ${yTop + rowH * 1.5}`}
                fill="none"
                stroke={C2}
                strokeWidth={2}
              />
              <polygon
                points={arrowHeadPoints(xr + 14, yTop + rowH * 1.25, xr, yTop + rowH * 1.5, 9)}
                fill={C2}
              />
              <HaloText x={xr + 28} y={yTop + rowH + 5} fontSize={13} fontWeight={700} fill={C2}>
                {`× ${show(k)}`}
              </HaloText>
            </g>
          );
        })()}
      {method === 'unit' && (
        <>
          {arc(x0 + cw * 0.5, x0 + cw * 1.5, yTop - 2, true, `÷ ${n(m.x1)}`, C2)}
          {arc(x0 + cw * 1.5, x0 + cw * 2.5, yTop - 2, true, `× ${n(m.x2)}`, C3)}
          {arc(x0 + cw * 0.5, x0 + cw * 1.5, yTop + 2 * rowH + 2, false, `÷ ${n(m.x1)}`, C2)}
          {arc(x0 + cw * 1.5, x0 + cw * 2.5, yTop + 2 * rowH + 2, false, `× ${n(m.x2)}`, C3)}
        </>
      )}
      {method === 'linear' &&
        (() => {
          const label = lf
            ? lf.up
              ? `× ${lf.k.p}`
              : `÷ ${lf.k.q}`
            : `× ${show(div(fx(m.x2), fx(m.x1)))}`;
          return (
            <>
              {arc(x0 + cw * 0.5, x0 + cw * 1.5, yTop - 2, true, label, C2)}
              {arc(x0 + cw * 0.5, x0 + cw * 1.5, yTop + 2 * rowH + 2, false, label, C2)}
            </>
          );
        })()}
      {method === 'cross' && (
        <>
          <line
            x1={x0 + cw * 0.3}
            y1={yTop + rowH * 1.5}
            x2={x0 + cw * 1.7}
            y2={yTop + rowH * 0.5}
            stroke={C2}
            strokeWidth={2}
            strokeDasharray="5 3"
          />
          <line
            x1={x0 + cw * 0.3}
            y1={yTop + rowH * 0.5}
            x2={x0 + cw * 1.7}
            y2={yTop + rowH * 1.5}
            stroke={C3}
            strokeWidth={2}
            strokeDasharray="5 3"
          />
          <HaloText
            x={VB_W / 2}
            y={yTop + 2 * rowH + 30}
            textAnchor="middle"
            fontSize={13}
            fill={INK}
          >
            {`? × ${n(m.x1)} = ${n(m.y1)} × ${n(m.x2)}`}
          </HaloText>
        </>
      )}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Partage                                                             */
/* ------------------------------------------------------------------ */

const SHARE_COLORS = [C1, C2, C3];

function ShareMode({ onSend, sendCost }: ToolProps) {
  const [total, setTotal] = useState(350);
  // à trois, 3 : 2 : 2 partage 350 en 7 parts de 50
  const [parts, setParts] = useState<number[]>([3, 2, 2]);
  const [three, setThree] = useState(false);
  const used = three ? parts.slice(0, 3) : parts.slice(0, 2);
  const r = shareSteps(total, used);
  return (
    <>
      <NumberField
        label="Total à partager"
        value={total}
        min={1}
        max={100000}
        step={10}
        decimals={2}
        onChange={setTotal}
      />
      <div className="grid grid-cols-2 gap-2">
        {used.map((p, i) => (
          <Stepper
            key={i}
            label={`Parts de ${String.fromCharCode(65 + i)}`}
            value={p}
            min={1}
            max={9}
            onChange={(v) => setParts(parts.map((x, j) => (j === i ? v : x)))}
          />
        ))}
      </div>
      <ToggleChip on={three} onClick={() => setThree((t) => !t)}>
        Trois personnes
      </ToggleChip>
      <MathText className="text-ink-900 block text-center text-xl">{`$\\text{ratio } ${used.join(' : ')}$`}</MathText>
      <ShareFigure total={total} parts={used} shares={r.shares} part={r.part} />
      <section className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3" aria-live="polite">
        <StepList steps={r.steps} testId="share-steps" />
        {onSend && (
          <div className="flex flex-wrap gap-2">
            {r.shares.map((s, i) => (
              <SendToSheetButton
                key={i}
                label={sendLabel(s)}
                cost={sendCost}
                onClick={() => onSend({ atoms: exact(s).atoms })}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function ShareFigure({
  total,
  parts,
  shares,
  part,
}: {
  total: number;
  parts: readonly number[];
  shares: Frac[];
  part: Frac;
}) {
  const count = parts.reduce((s, p) => s + p, 0);
  const x0 = 14;
  const W = VB_W - 28;
  const pw = W / count;
  const y = 34;
  const h = 34;
  // étiquettes trop larges pour leur part : une sur deux descend d'une ligne
  const texts = parts.map((_, i) => `${String.fromCharCode(65 + i)} : ${show(shares[i]!)}`);
  const crowded = parts.some((p, i) => texts[i]!.length * 7.5 > p * pw);
  const H = crowded ? 138 : 120;
  let k = 0;
  const label = show(part);
  const fits = label.length * 7.5 + 4 <= pw;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`${n(total)} partagé en ${count} parts de ${label} : ${parts.map((p, i) => `${String.fromCharCode(65 + i)} reçoit ${show(shares[i]!)}`).join(', ')}.`}
    >
      <path
        d={`M ${x0} ${y - 6} L ${x0} ${y - 14} L ${x0 + W} ${y - 14} L ${x0 + W} ${y - 6}`}
        fill="none"
        stroke={MUTED}
        strokeWidth={1.5}
      />
      <text x={x0 + W / 2} y={y - 19} textAnchor="middle" fontSize={14} fontWeight={700} fill={INK}>
        {n(total)}
      </text>
      {parts.map((p, i) => {
        const start = k;
        k += p;
        const xa = x0 + start * pw;
        const xb = x0 + k * pw;
        const c = SHARE_COLORS[i]!;
        return (
          <g key={i}>
            {Array.from({ length: p }, (_, j) => (
              <g key={j}>
                <rect
                  x={xa + j * pw}
                  y={y}
                  width={pw}
                  height={h}
                  fill={c}
                  fillOpacity={0.25}
                  stroke={c}
                  strokeWidth={1.5}
                />
                {fits && (
                  <text
                    x={xa + (j + 0.5) * pw}
                    y={y + h / 2 + 5}
                    textAnchor="middle"
                    fontSize={12}
                    fill={INK}
                  >
                    {label}
                  </text>
                )}
              </g>
            ))}
            <path
              d={`M ${xa + 2} ${y + h + 6} L ${xa + 2} ${y + h + 12} L ${xb - 2} ${y + h + 12} L ${xb - 2} ${y + h + 6}`}
              fill="none"
              stroke={c}
              strokeWidth={2}
            />
            <text
              x={Math.min(
                VB_W - 4 - (texts[i]!.length * 7.5) / 2,
                Math.max(4 + (texts[i]!.length * 7.5) / 2, (xa + xb) / 2),
              )}
              y={y + h + 30 + (crowded && i % 2 === 1 ? 18 : 0)}
              textAnchor="middle"
              fontSize={13}
              fontWeight={700}
              fill={INK}
            >
              {texts[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
